/**
 * Normalizador de dados para Histórico
 * Converte resposta da API timeline para formato de exibição
 */

import type {
	HistoricoItem,
	TimelineItem,
	TimelineResponse,
	TimelineSourceType,
} from "../types/historico";

/**
 * Normaliza um item do timeline para o formato de exibição
 */
export function normalizeTimelineItem(item: TimelineItem): HistoricoItem {
	const id = `${item.source}-${item.id}`;

	// User label: preferir user_nome, fallback para user_login
	const userLabel = item.user_nome || item.user_login || "N/A";

	// Subtitle: cliente_nome ou fallback
	const subtitle = item.cliente_nome || "Cliente não informado";

	// Status/fluxo baseado no source
	let status: string;
	if (item.source === "orcamentos_temp") {
		status = item.meta?.fluxo || "temp";
	} else {
		status = item.meta?.status || "salvo";
	}

	// Path com fallback
	const path = item.caminho || "";

	return {
		id,
		source: item.source,
		originalId: item.id,
		timestamp: item.created_at,
		title: item.nome_arquivo || "Arquivo sem nome",
		subtitle,
		path,
		userLabel,
		status,
		favorite: item.meta?.favorito,
		meta: {
			cliente_id: item.cliente_id,
			user_id: item.user_id,
			pedido_id: item.meta?.pedido_id,
			item_id: item.meta?.item_id,
			produto: item.meta?.produto,
			categoria_dir: item.meta?.categoria_dir,
			empresa: item.meta?.empresa,
			// Preservar todos os metadados originais
			...item.meta,
		},
	};
}

/**
 * Normaliza resposta completa do timeline
 */
export function normalizeTimelineResponse(
	response: TimelineResponse
): HistoricoItem[] {
	if (!response.success || !response.data?.items) {
		return [];
	}

	return response.data.items.map((item) => normalizeTimelineItem(item));
}

/**
 * Ordena items por timestamp DESC
 * Remove duplicatas por id
 */
export function sortItems(items: HistoricoItem[]): HistoricoItem[] {
	const uniqueMap = new Map<string, HistoricoItem>();

	// Adiciona itens, mantendo apenas o último em caso de duplicata
	items.forEach((item) => {
		uniqueMap.set(item.id, item);
	});

	// Converte para array e ordena por timestamp DESC
	const sorted = Array.from(uniqueMap.values()).sort((a, b) => {
		const dateA = new Date(a.timestamp).getTime();
		const dateB = new Date(b.timestamp).getTime();
		return dateB - dateA; // DESC
	});

	return sorted;
}

/**
 * Filtra items por source
 */
export function filterBySource(
	items: HistoricoItem[],
	source: TimelineSourceType | "all"
): HistoricoItem[] {
	if (source === "all") {
		return items;
	}
	return items.filter((item) => item.source === source);
}

/**
 * Filtra items por termo de busca (client-side)
 */
export function filterBySearchTerm(
	items: HistoricoItem[],
	searchTerm: string
): HistoricoItem[] {
	if (!searchTerm || !searchTerm.trim()) {
		return items;
	}

	const searchLower = searchTerm.toLowerCase().trim();

	return items.filter(
		(item) =>
			item.title.toLowerCase().includes(searchLower) ||
			item.subtitle.toLowerCase().includes(searchLower) ||
			item.path.toLowerCase().includes(searchLower) ||
			item.userLabel.toLowerCase().includes(searchLower) ||
			item.status.toLowerCase().includes(searchLower)
	);
}

/**
 * Retorna label amigável para o source
 */
export function getSourceLabel(source: TimelineSourceType | "all"): string {
	const labels: Record<TimelineSourceType | "all", string> = {
		all: "Todos",
		file_save: "Orçamento",
		pre_file_save: "Leads",
		orcamentos_temp: "Orçamento Old",
	};
	return labels[source] || source;
}

/**
 * Retorna cor do badge para o source
 */
export function getSourceColor(
	source: TimelineSourceType
): "blue" | "green" | "yellow" | "gray" {
	const colors: Record<TimelineSourceType, "blue" | "green" | "yellow"> = {
		file_save: "blue",
		pre_file_save: "green",
		orcamentos_temp: "yellow",
	};
	return colors[source] || "gray";
}
